// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module DashboardBuilder
 * @package @geenius/analytics/react
 * @description Renders the React dashboard builder that composes heterogeneous
 * analytics widgets into a page-ready grid. The builder consumes the shared
 * dashboard layout vocabulary so consumers can declare dashboards as data.
 */

import type {
  CohortDashboardWidget,
  DashboardLayout,
  DashboardWidget,
  FunnelDashboardWidget,
  ReplayDashboardWidget,
  RealtimeDashboardWidget,
  TimeRange,
} from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@geenius/ui/react";
import type React from "react";
import { useMemo, useState } from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import { ChartCard } from "./ChartCard";
import { DeviceBreakdown } from "./DeviceBreakdown";
import { EventTable } from "./EventTable";
import { FunnelChart } from "./FunnelChart";
import { RealtimeBadge } from "./RealtimeBadge";
import { StatsCard } from "./StatsCard";
import { TopPagesTable } from "./TopPagesTable";

type AnalyticsCopy = ReturnType<typeof useAnalyticsCopy>;

/**
 * Props for rendering the React dashboard builder.
 *
 * @property layout Shared dashboard layout definition or a raw widget array.
 * @property className Optional class name merged into the section wrapper.
 * @property editable Enables accessible widget reordering controls.
 * @property timeRangeOptions Optional time-range choices rendered in the builder toolbar.
 * @property selectedTimeRangeId Currently selected time-range option id.
 * @property onTimeRangeChange Called when the selected time range changes.
 * @property onLayoutChange Called with the reordered widget array after drag/drop or button movement.
 */
export interface DashboardBuilderProps {
  layout: DashboardLayout | DashboardWidget[];
  className?: string;
  editable?: boolean;
  timeRangeOptions?: DashboardBuilderTimeRangeOption[];
  selectedTimeRangeId?: string;
  onTimeRangeChange?: (option: DashboardBuilderTimeRangeOption) => void;
  onLayoutChange?: (widgets: DashboardWidget[]) => void;
}

/**
 * Time-range option rendered by the dashboard builder toolbar.
 *
 * @property id Stable option id used by the select control.
 * @property label Human-readable range label.
 * @property range Inclusive analytics range applied by the host dashboard.
 */
export interface DashboardBuilderTimeRangeOption {
  id: string;
  label: string;
  range: TimeRange;
}

function moveWidget(
  widgets: DashboardWidget[],
  widgetId: string,
  direction: -1 | 1,
): DashboardWidget[] {
  const index = widgets.findIndex((widget) => widget.id === widgetId);
  const nextIndex = index + direction;

  if (index < 0 || nextIndex < 0 || nextIndex >= widgets.length) {
    return widgets;
  }

  const nextWidgets = [...widgets];
  const [moved] = nextWidgets.splice(index, 1);

  if (!moved) {
    return widgets;
  }

  nextWidgets.splice(nextIndex, 0, moved);
  return nextWidgets;
}

function dropWidget(
  widgets: DashboardWidget[],
  draggedId: string,
  targetId: string,
): DashboardWidget[] {
  const fromIndex = widgets.findIndex((widget) => widget.id === draggedId);
  const toIndex = widgets.findIndex((widget) => widget.id === targetId);

  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return widgets;
  }

  const nextWidgets = [...widgets];
  const [moved] = nextWidgets.splice(fromIndex, 1);

  if (!moved) {
    return widgets;
  }

  nextWidgets.splice(toIndex, 0, moved);
  return nextWidgets;
}

function renderCohort(
  widget: CohortDashboardWidget,
  copy: AnalyticsCopy,
): React.ReactElement {
  return (
    <Card data-analytics-dashboard-widget data-widget-kind="cohort">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-0">
        <div className="space-y-1">
          <CardTitle className="text-sm" data-analytics-dashboard-widget-title>
            {widget.title}
          </CardTitle>
          <CardDescription>
            {copy.plural(
              "analytics.dashboardBuilder.cohortDescription",
              widget.analysis.intervals.length,
            )}
          </CardDescription>
        </div>
        <div
          className="flex flex-wrap items-center gap-2"
          data-analytics-cohort-overview
        >
          <Badge variant="secondary">
            {copy.plural(
              "analytics.dashboardBuilder.cohorts",
              widget.analysis.cohorts.length,
            )}
          </Badge>
          <Badge variant="secondary" data-analytics-cohort-intervals>
            {copy.t("analytics.dashboardBuilder.dayIntervals", {
              intervals: widget.analysis.intervals.join(", "),
            })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent
        className="grid gap-3 pt-4 md:grid-cols-2"
        data-analytics-cohort-grid
      >
        {widget.analysis.cohorts.map((cohort) => (
          <Card
            key={cohort.cohort}
            className="border-muted/80 bg-muted/30 shadow-none"
            data-analytics-cohort-card
          >
            <CardContent className="grid gap-3 pt-6">
              <div className="flex items-center justify-between gap-3">
                <strong className="text-sm font-semibold text-foreground">
                  {cohort.cohort}
                </strong>
                <Badge variant="secondary">
                  {copy.plural("analytics.dashboardBuilder.users", cohort.size)}
                </Badge>
              </div>
              <div
                className="flex flex-col gap-2"
                data-analytics-cohort-retention
              >
                {cohort.retained.map((point) => (
                  <div
                    key={`${cohort.cohort}-${point.day}`}
                    className="flex items-center justify-between gap-3 rounded-lg bg-background px-3 py-2 text-xs text-muted-foreground"
                    data-analytics-cohort-point
                  >
                    <span className="font-medium text-foreground">
                      {copy.t("analytics.dashboardBuilder.dayLabel", {
                        day: point.day,
                      })}
                    </span>
                    <span>{point.retentionRate.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </CardContent>
    </Card>
  );
}

function renderReplay(
  widget: ReplayDashboardWidget,
  copy: AnalyticsCopy,
): React.ReactElement {
  return (
    <Card data-analytics-dashboard-widget data-widget-kind="replay">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-0">
        <div className="space-y-1">
          <CardTitle className="text-sm" data-analytics-dashboard-widget-title>
            {widget.title}
          </CardTitle>
          <CardDescription>
            {copy.t("analytics.dashboardBuilder.replayDescription")}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            {copy.plural(
              "analytics.dashboardBuilder.frames",
              widget.replay.frames.length,
            )}
          </Badge>
          <Badge variant="secondary">
            {copy.t("analytics.dashboardBuilder.durationSeconds", {
              seconds: Math.round(widget.replay.duration / 1000),
            })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        <ol className="grid gap-3" data-analytics-session-replay-list>
          {widget.replay.frames.map((frame) => (
            <li
              key={frame.id}
              className="grid gap-2 rounded-xl border border-border bg-muted/40 p-4 text-sm"
              data-analytics-session-replay-frame
            >
              <div className="flex items-center justify-between gap-3">
                <strong className="text-foreground">{frame.label}</strong>
                <span className="text-xs text-muted-foreground">
                  {copy.formatTime(frame.timestamp)}
                </span>
              </div>
              <span className="truncate text-xs text-muted-foreground">
                {frame.url}
              </span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function renderFunnel(widget: FunnelDashboardWidget): React.ReactElement {
  return (
    <Card data-analytics-dashboard-widget data-widget-kind="funnel">
      <CardHeader className="pb-0">
        <CardTitle className="text-sm" data-analytics-dashboard-widget-title>
          {widget.title}
        </CardTitle>
        {widget.description && (
          <CardDescription>{widget.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-4">
        <FunnelChart result={widget.result} />
      </CardContent>
    </Card>
  );
}

function renderRealtime(
  widget: RealtimeDashboardWidget,
  copy: AnalyticsCopy,
): React.ReactElement {
  return (
    <Card data-analytics-dashboard-widget data-widget-kind="realtime">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-0">
        <div className="space-y-1">
          <CardTitle className="text-sm" data-analytics-dashboard-widget-title>
            {widget.title}
          </CardTitle>
          {widget.description && (
            <CardDescription>{widget.description}</CardDescription>
          )}
        </div>
        <RealtimeBadge
          stats={widget.stats}
          isLoading={widget.isLoading ?? false}
        />
      </CardHeader>
      <CardContent className="grid gap-3 pt-4 text-sm text-muted-foreground">
        {widget.stats ? (
          <>
            <div className="flex items-center justify-between gap-3">
              <span>{copy.t("analytics.realtime.currentPageViews")}</span>
              <strong className="text-foreground">
                {widget.stats.currentPageViews}
              </strong>
            </div>
            <div className="grid gap-2" data-analytics-realtime-pages>
              {widget.stats.topActivePages.map((page) => (
                <div
                  key={page.url}
                  className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2"
                >
                  <span className="truncate">{page.url}</span>
                  <Badge variant="secondary">{page.visitors}</Badge>
                </div>
              ))}
            </div>
          </>
        ) : (
          <span>{copy.t("analytics.realtime.empty")}</span>
        )}
      </CardContent>
    </Card>
  );
}

function renderWidget(
  widget: DashboardWidget,
  copy: AnalyticsCopy,
): React.ReactElement | null {
  switch (widget.kind) {
    case "metric":
      return (
        <StatsCard
          label={widget.title}
          value={widget.metric.value}
          change={
            widget.metric.changePercent !== undefined
              ? `${widget.metric.changePercent.toFixed(1)}%`
              : undefined
          }
          trend={
            widget.metric.trend === "flat" ? "neutral" : widget.metric.trend
          }
        />
      );
    case "chart":
      return (
        <ChartCard
          title={widget.title}
          data={widget.data}
          type={widget.chartType}
        />
      );
    case "top-pages":
      return <TopPagesTable pages={widget.pages} />;
    case "device-breakdown":
      return <DeviceBreakdown breakdown={widget.breakdown} />;
    case "events":
      return <EventTable events={widget.events} maxRows={widget.maxRows} />;
    case "funnel":
      return renderFunnel(widget);
    case "realtime":
      return renderRealtime(widget, copy);
    case "cohort":
      return renderCohort(widget, copy);
    case "replay":
      return renderReplay(widget, copy);
    default:
      return null;
  }
}

/**
 * Renders the React dashboard builder from a shared layout definition.
 *
 * @param props Dashboard layout data and an optional wrapper className.
 * @returns A section element containing the resolved analytics widget grid.
 */
export function DashboardBuilder({
  layout,
  className,
  editable = false,
  timeRangeOptions = [],
  selectedTimeRangeId,
  onTimeRangeChange,
  onLayoutChange,
}: DashboardBuilderProps): React.ReactElement {
  const resolved = Array.isArray(layout) ? { widgets: layout } : layout;
  const copy = useAnalyticsCopy();
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const selectedRangeId = selectedTimeRangeId ?? timeRangeOptions[0]?.id ?? "";
  const canEditLayout = editable && typeof onLayoutChange === "function";
  const orderedWidgets = useMemo(() => resolved.widgets, [resolved.widgets]);

  const emitMove = (widgetId: string, direction: -1 | 1): void => {
    if (!canEditLayout) {
      return;
    }

    const nextWidgets = moveWidget(orderedWidgets, widgetId, direction);

    if (nextWidgets !== orderedWidgets) {
      onLayoutChange(nextWidgets);
    }
  };

  const emitDrop = (targetId: string): void => {
    if (!canEditLayout || !draggedWidgetId) {
      setDraggedWidgetId(null);
      return;
    }

    const nextWidgets = dropWidget(orderedWidgets, draggedWidgetId, targetId);

    if (nextWidgets !== orderedWidgets) {
      onLayoutChange(nextWidgets);
    }

    setDraggedWidgetId(null);
  };

  const emitTimeRangeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const nextOption = timeRangeOptions.find(
      (option) => option.id === event.currentTarget.value,
    );

    if (nextOption) {
      onTimeRangeChange?.(nextOption);
    }
  };

  return (
    <section
      className={cn("flex flex-col gap-4", className)}
      data-analytics-dashboard-builder
    >
      {(resolved.title || resolved.description) && (
        <header
          className="flex flex-col gap-1"
          data-analytics-dashboard-builder-header
        >
          {resolved.title && (
            <h2 className="text-lg font-semibold tracking-tight">
              {resolved.title}
            </h2>
          )}
          {resolved.description && (
            <p className="text-sm text-muted-foreground">
              {resolved.description}
            </p>
          )}
        </header>
      )}
      {(timeRangeOptions.length > 0 || canEditLayout) && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-muted/30 px-3 py-2"
          data-analytics-dashboard-builder-toolbar
        >
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {timeRangeOptions.length > 0 && (
              <label className="flex items-center gap-2">
                <span>{copy.t("analytics.dashboardBuilder.timeRange")}</span>
                <select
                  aria-label={copy.t(
                    "analytics.dashboardBuilder.timeRangeAria",
                  )}
                  className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground"
                  value={selectedRangeId}
                  onChange={emitTimeRangeChange}
                >
                  {timeRangeOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {canEditLayout && (
            <span
              className="text-xs font-medium text-muted-foreground"
              data-analytics-dashboard-builder-editing
            >
              {copy.t("analytics.dashboardBuilder.reorderWidgets")}
            </span>
          )}
        </div>
      )}
      <div
        className="grid gap-4 md:grid-cols-2"
        data-analytics-dashboard-builder-grid
      >
        {orderedWidgets.map((widget, index) => (
          <div
            key={widget.id}
            className={cn((widget.span ?? 1) > 1 && "md:col-span-2")}
            data-analytics-dashboard-builder-cell
            data-span={widget.span ?? 1}
            draggable={canEditLayout}
            onDragStart={() => setDraggedWidgetId(widget.id)}
            onDragOver={(event) => {
              if (canEditLayout) {
                event.preventDefault();
              }
            }}
            onDrop={() => emitDrop(widget.id)}
          >
            {canEditLayout && (
              <div
                className="mb-2 flex items-center justify-end gap-2"
                data-analytics-dashboard-builder-cell-actions
              >
                <button
                  type="button"
                  className="rounded-lg border border-border bg-background px-2 py-1 text-xs font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={index === 0}
                  onClick={() => emitMove(widget.id, -1)}
                >
                  {copy.t("analytics.dashboardBuilder.moveUp")}
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-border bg-background px-2 py-1 text-xs font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={index === orderedWidgets.length - 1}
                  onClick={() => emitMove(widget.id, 1)}
                >
                  {copy.t("analytics.dashboardBuilder.moveDown")}
                </button>
              </div>
            )}
            {renderWidget(widget, copy)}
          </div>
        ))}
      </div>
    </section>
  );
}
